import Cookies from 'js-cookie'
import * as jose from 'jose'
import * as fingerprint from '@rajesh896/broprint.js'
import {
  ACCESS_TOKEN_COOKIE,
  ACCESS_TOKEN_DEFAULT_EXPIRE_TIME,
  APP_AUTH_URL,
  ENDPOINT_API_URL,
  APP_ENVIRONMENT,
  APP_ENVIRONMENT_COOKIE,
  REFRESH_TOKEN_COOKIE,
  REFRESH_TOKEN_DEFAULT_EXPIRE_TIME,
  WILDCARDS_DOMAIN,
  REDIRECT_URL_COOKIE,
  REDIRECT_URL_DEFAULT_EXPIRE_TIME,
  FINGERPRINT_COOKIE,
  ACCOUNT_ID_COOKIE,
  APP_SUSPENDED_URL,
  USERNAME_COOKIE,
  USER_ID_COOKIE,
  APP_SUCCESS_REDIRECT_DEFAULT,
} from "./common/const";
import AnalyticsService from "@emcd-shared/analytics-service";
import StreamService from './services/stream';
import { getMainDomain } from './utils/extractDomain'

let authInstance

export class AuthServiceCore {
  env
  instance
  fingerprint
  redirectUrl
  hostUrl
  systemInfo
  userData

  constructor() {
    this.env = APP_ENVIRONMENT
    Cookies.set(APP_ENVIRONMENT_COOKIE, APP_ENVIRONMENT)
    this.hostUrl = new URL(APP_AUTH_URL)
    this.location = new URL(window.location.href)
    this.suspendedUrl = new URL(APP_SUSPENDED_URL)
    this.redirectUrl = AuthServiceCore.getRedirectUrl()
    this.identify()
  }

  static getInstance() {
    if (!authInstance) {
      authInstance = new AuthServiceCore()
    }
    return authInstance
  }

  static getDomain() {
    return getMainDomain()
  }

  static getRedirectUrl() {
    AnalyticsService.addBreadcrumb('AuthService', 'getRedirectUrl')

    const defaultUrl = new URL(APP_SUCCESS_REDIRECT_DEFAULT) //  https://emcd.io/wallets
    const location = new URL(window.location.href)
    const locationUrlParam = location.searchParams.get('redirect') // берем все из ?redirect=
    const cookieRedirect = Cookies.get(REDIRECT_URL_COOKIE) // берем все из куки

    let targetUrl = defaultUrl

    // проверяем все доступные url
    if (locationUrlParam) {
      targetUrl = new URL(locationUrlParam)
    } else if (cookieRedirect) {
      const cookieUrl = new URL(cookieRedirect)
      const cookieUrlParams = cookieUrl.searchParams.get('redirect')

      if (cookieUrlParams) {
        targetUrl = new URL(cookieUrlParams)
      }
    }

    // проверяем вхождение targetUrl в whitelist
    const { hostname } = targetUrl
    if (hostname.match(WILDCARDS_DOMAIN)) {
      return targetUrl.toString()
    }
    return defaultUrl.toString()
  }

  static getAccessToken() {
    return Cookies.get(ACCESS_TOKEN_COOKIE)
  }

  static getRefreshToken() {
    return Cookies.get(REFRESH_TOKEN_COOKIE)
  }

  static deleteAccessToken() {
    const mainDomain = AuthServiceCore.getDomain()
    if (mainDomain) {
      Cookies.remove(ACCESS_TOKEN_COOKIE, { domain: mainDomain })
    }
    const currentDomain = window.location.hostname
    if (currentDomain !== mainDomain) {
      Cookies.remove(ACCESS_TOKEN_COOKIE, { domain: currentDomain })
    }
    Cookies.remove(ACCESS_TOKEN_COOKIE)
  }

  static deleteRefreshToken() {
    const mainDomain = AuthServiceCore.getDomain()
    if (mainDomain) {
      Cookies.remove(REFRESH_TOKEN_COOKIE, { domain: mainDomain })
    }
    const currentDomain = window.location.hostname
    if (currentDomain !== mainDomain) {
      Cookies.remove(REFRESH_TOKEN_COOKIE, { domain: currentDomain })
    }
    Cookies.remove(REFRESH_TOKEN_COOKIE)
  }

  static parseToken(token) {
    AnalyticsService.addBreadcrumb('AuthService', 'parseToken')
    try {
      return jose.decodeJwt(token)
    } catch (e) {
      AnalyticsService.error(e)
      return false
    }
  }

  static isAuthorized() {
    return !!AuthServiceCore.getAccessToken() && !!AuthServiceCore.getRefreshToken()
  }

  static isPossibleAuthorize() {
    return !!AuthServiceCore.getAccessToken() && !!AuthServiceCore.getRefreshToken()
  }

  static setRedirectUrl(url) {
    AnalyticsService.addBreadcrumb('AuthService', 'setRedirectUrl')
    let redirectUrl
    if (url) {
      redirectUrl = new URL(url)
    } else {
      redirectUrl = new URL(window.location.href)
    }
    if ([APP_SUSPENDED_URL, APP_AUTH_URL, `${APP_AUTH_URL}/authorizer`].includes(redirectUrl.toString())) {
      // проверка URL:
      // https://emcd.io/auth/suspended || https://emcd.io/auth || https://emcd.io/auth/authorizer
      redirectUrl = new URL(APP_SUCCESS_REDIRECT_DEFAULT)
    }

    // проверяем URL: если подтверждение 2фа, телефонов, номеров и не залогинен - после логина отправляем его в настройки
    if (redirectUrl.toString().includes('/confirm') && !this.isAuthorized()) {
      const BASE_URL = globalThis?.import_meta_env?.BASE_URL || process.env.BASE_URL || ''
      const baseUrl = new URL(BASE_URL, window.location.origin)

      redirectUrl = new URL('security', baseUrl.toString())
    }

    let cookieDomain = redirectUrl.hostname
    if (cookieDomain.match(WILDCARDS_DOMAIN)) { // если хост в списке разрешенных (emcd.io | localhost)
      Cookies.set(REDIRECT_URL_COOKIE, redirectUrl.toString(), { // запись в куку полного URL
        domain: AuthServiceCore.getDomain(),
        expires: REDIRECT_URL_DEFAULT_EXPIRE_TIME, // 7 дней
      })
      return redirectUrl
    } else {
      AnalyticsService.error(`Unsupported domain: ${cookieDomain}`)
      return null

    }
  }

  static deleteRedirectUrl() {
    const mainDomain = AuthServiceCore.getDomain()
    if (mainDomain) {
      Cookies.remove(REDIRECT_URL_COOKIE, { domain: mainDomain })
    }
    const currentDomain = window.location.hostname
    if (currentDomain !== mainDomain) {
      Cookies.remove(REDIRECT_URL_COOKIE, { domain: currentDomain })
    }
    Cookies.remove(REDIRECT_URL_COOKIE)
  }

  static logout() {
    AnalyticsService.addBreadcrumb('AuthService', 'logout')

    AuthServiceCore.clearSessionCookie()
    AnalyticsService.setAuthStatus(false)
    const redirectUrl = AuthServiceCore.setRedirectUrl()
    AnalyticsService.setContextVariable('redirect', redirectUrl.toString())
    const loginUrl = new URL(APP_AUTH_URL) // https://emcd.io/auth
    if (window.location.hostname === 'localhost') {
      const BASE_URL = globalThis?.import_meta_env?.BASE_URL || process.env.BASE_URL || ''
      const baseUrl = new URL(BASE_URL, window.location.origin)
      const authorizerUrl = new URL('authorizer', baseUrl.toString())
      loginUrl.searchParams.set('authorizer', authorizerUrl.toString())
    }
    loginUrl.searchParams.set('redirect', redirectUrl.toString())
    AnalyticsService.setContextVariable('loginUrl', loginUrl.toString())
    window.location.href = loginUrl
  }

  static clearSessionCookie() {
    AuthServiceCore.deleteAccessToken()
    AuthServiceCore.deleteRefreshToken()
    const mainDomain = AuthServiceCore.getDomain()
    if (mainDomain) {
      Cookies.remove(ACCOUNT_ID_COOKIE, { domain: mainDomain })
      Cookies.remove(USER_ID_COOKIE, { domain: mainDomain })
      Cookies.remove(USERNAME_COOKIE, { domain: mainDomain })
    }
    const currentDomain = window.location.hostname
    if (currentDomain !== mainDomain) {
      Cookies.remove(ACCOUNT_ID_COOKIE, { domain: currentDomain })
      Cookies.remove(USER_ID_COOKIE, { domain: currentDomain })
      Cookies.remove(USERNAME_COOKIE, { domain: currentDomain })
    }
    Cookies.remove(ACCOUNT_ID_COOKIE)
    Cookies.remove(USER_ID_COOKIE)
    Cookies.remove(USERNAME_COOKIE)
  }

  getAccessToken() {
    return AuthServiceCore.getAccessToken()
  }

  fetchRefresh(axiosInstance, saveDataCallBack) {
    AnalyticsService.addBreadcrumb('AuthService.refresh', {
      status: 'start',
      description: `Run refresh operation`,
    })
    return new Promise((resolve, reject) => {
      AnalyticsService.addBreadcrumb('AuthService.refresh', {
        status: 'in process',
        description: `Check is access & refresh token exist in client`,
      })
      if (!AuthServiceCore.isPossibleAuthorize()) {
        AnalyticsService.addBreadcrumb('AuthService.refresh', {
          status: 'reject',
          description: `Tokens is not exist on client, make logout and Unauthorised`,
        })
        AnalyticsService.warning('AuthService.refresh.isPossibleAuthorize')
        this.logout()
        return reject('Unauthorised')
      }
      if (!axiosInstance) {
        AnalyticsService.addBreadcrumb('AuthService.refresh', {
          status: 'reject',
          description: `Axios instance is not defined, make axiosInstance error`,
        })
        AnalyticsService.error('AuthService.refresh.axiosInstance is empty')
        return reject('axiosInstance error')
      }
      const axiosEndpointUrl = ENDPOINT_API_URL
      const access_token = this.getAccessToken()
      const refresh_token = this.getRefreshToken()
      AnalyticsService.addBreadcrumb('AuthService.refresh', {
        status: 'in process',
        description: "Check is access_token and refresh_token exist from cookie",
      })
      if (access_token && refresh_token) {
        AnalyticsService.addBreadcrumb('AuthService.refresh', {
          status: 'in process',
          description: "access_token and refresh_token is exist, fetch new tokens - /auth/refresh",
          access_token: access_token.slice(-5),
          refresh_token: refresh_token.slice(-5)
        })
        axiosInstance
          .post(`${axiosEndpointUrl}/auth/refresh`, {
            access_token,
            refresh_token,
          })
          .then((res) => {
            AnalyticsService.addBreadcrumb('AuthService.refresh', {
              status: 'in process',
              description: "Success fetch - /auth/refresh",
            })
            AnalyticsService.addBreadcrumb('AuthService', 'refresh.response')
            const access_token = res?.data?.access_token || res?.access_token
            const refresh_token = res?.data?.refresh_token || res?.refresh_token
            AnalyticsService.addBreadcrumb('AuthService.refresh', {
              status: 'in process',
              description: "Check is response have token",
            })
            if (!access_token || !refresh_token) {
              AnalyticsService.addBreadcrumb('AuthService.refresh', {
                status: 'reject',
                description: "access_token and refresh_token is not defined, make Unauthorised",
                new_access_token: access_token.slice(-5),
                new_refresh_token: refresh_token.slice(-5)
              })
              AnalyticsService.error('Refresh Bad response')
              AnalyticsService.log(res)
              return reject('Unauthorised')
            }
            AnalyticsService.addBreadcrumb('AuthService.refresh', {
              status: 'in process',
              description: "access_token and refresh_token is defined",
            })
            try {
              const parsedToken = AuthServiceCore.parseToken(access_token)
              this.setAccessToken(access_token)
              this.setRefreshToken(refresh_token)

              AnalyticsService.addBreadcrumb('AuthService.refresh', {
                status: 'in process',
                description: "check is parse token is suspended",
                expireNewAccessToken: parsedToken.exp
              })

              if (parsedToken?.suspended) {
                AnalyticsService.addBreadcrumb('AuthService.refresh', {
                  status: 'reject',
                  description: "Token have suspended, make suspended",
                })
                AnalyticsService.setContextVariable('fromUrl', window.location.href)
                window.location.replace(this.suspendedUrl.toString())
                return reject('Suspended')
              }

              const result = {
                access_token,
                refresh_token,
                userData: parsedToken,
                systemInfo: this.systemInfo,
                fingerprint: this.fingerprint,
              }

              AnalyticsService.addBreadcrumb('AuthService.refresh', {
                status: 'in process',
                description: "Check is need call function after fetch - refresh token",
              })

              if (typeof saveDataCallBack === 'function') {
                AnalyticsService.addBreadcrumb('AuthService.refresh', {
                  status: 'in process',
                  description: `Call function ${saveDataCallBack.name}`,
                })
                saveDataCallBack(result)
              }

              AnalyticsService.addBreadcrumb('AuthService.refresh', {
                status: 'resolve',
                description: "Is no function to call, resolve refresh",
              })

              return resolve(result)
            } catch (e) {
              AnalyticsService.addBreadcrumb('AuthService.refresh', {
                status: 'reject',
                description: "Unexpect error, make Unauthorised",
              })
              AnalyticsService.error(e)
              return reject('Unauthorised')
            }
          })
          .catch((e) => {
            AnalyticsService.addBreadcrumb('AuthService.refresh', {
              status: 'reject',
              error: e,
              description: "Get error to refresh tokens - /auth/refresh, make Unauthorised",
            })
            if (e.response) {
              AnalyticsService.log(e.response?.status)
              AnalyticsService.log(JSON.stringify(e.request?.data))
              AnalyticsService.log(e.response?.headers)
            }
            if (e.request) {
              AnalyticsService.log(e.request?.status)
              AnalyticsService.log(JSON.stringify(e.request?.data))
              AnalyticsService.log(e.request.headers)
            }
            AnalyticsService.error(e)
            return reject('Unauthorised')
          })
      } else {
        AnalyticsService.addBreadcrumb('AuthService.refresh', {
          status: 'reject',
          description: "Access_token and refresh_token is not exist in cookie, make Unauthorised",
        })
        AnalyticsService.error('Missed access & refresh tokens in cookie')
        return reject('Unauthorised')
      }
    });
  }

  refresh(axiosInstance, saveDataCallBack) {
    const isOpeStream = StreamService.isOpen();

    if (!isOpeStream) {
      AnalyticsService.addBreadcrumb('AuthService.refresh', {
        description: "Save promise stream",
      })
      StreamService.promise = this.fetchRefresh(
        axiosInstance,
        saveDataCallBack
      );
    }

    return StreamService.promise
      .then((res) => {
        AnalyticsService.addBreadcrumb('AuthService.refresh', {
          description: "StreamService.promise is resolve",
        })
        StreamService.syncPagesSteams('resolve');
        return res;
      })
      .finally(() => StreamService.close());
  }

  getRefreshToken() {
    return AuthServiceCore.getRefreshToken()
  }

  isPossibleAuthorize() {
    return AuthServiceCore.isPossibleAuthorize()
  }

  identify() {
    AnalyticsService.addBreadcrumb('AuthService.identify', {
      status: 'start',
      description: "Run user identify",
    })
    return new Promise((resolve, reject) => {
      this.systemInfo = {
        userAgent: navigator.userAgent,
        language: navigator.language,
        webdriver: navigator.webdriver,
        cookieEnabled: navigator.cookieEnabled,
      }
      const result = {
        systemInfo: this.systemInfo,
        fingerprint: Cookies.get(FINGERPRINT_COOKIE),
      }
      if (result.fingerprint) {
        AnalyticsService.addBreadcrumb('AuthService.identify', {
          status: 'resolve',
          description: "fingerprint be in cookie, identify success",
        })
        return resolve(result)
      } else {
        AnalyticsService.addBreadcrumb('AuthService.identify', {
          status: 'in process',
          description: "fingerprint in not defined in cookie, run generate fingerprint",
        })
        fingerprint
          .getCurrentBrowserFingerPrint()
          .then((res) => {
            this.fingerprint = res
            this.setFingerprint(res)
            const result = {
              systemInfo: this.systemInfo,
              fingerprint: this.fingerprint,
            }
            AnalyticsService.addBreadcrumb('AuthService.identify', {
              status: 'resolve',
              description: "generate fingerprint is success",
            })
            return resolve(result)
          })
          .catch((e) => {
            AnalyticsService.addBreadcrumb('AuthService.identify', {
              status: 'reject',
              error: e,
              description: "unavailable browser fingerprint",
            })
            AnalyticsService.error(e)
            return reject('Unavailable browser fingerprint')
          })
      }
    })
  }

  authenticate() {
    return new Promise((resolve, reject) => {
      AnalyticsService.addBreadcrumb('AuthService.authenticate', {
        status: 'start',
        description: `Check is access & refresh token exist in cookie`,
      })
      if (AuthServiceCore.isAuthorized()) {
        AnalyticsService.addBreadcrumb('AuthService.authenticate', {
          status: 'in process',
          description: `Tokens exist in cookie`,
        })
        const access_token = AuthServiceCore.getAccessToken()
        const parsedToken = AuthServiceCore.parseToken(access_token)
        const isAccessTokenExpired = parsedToken.exp * 1000 < new Date()
        AnalyticsService.addBreadcrumb('AuthService.authenticate', {
          status: 'in process',
          description: `Check is access token expired`,
          expireToken: parsedToken.exp,
          now: Date.now()
        })
        if (isAccessTokenExpired) {
          AnalyticsService.addBreadcrumb('AuthService.authenticate', {
            status: 'reject',
            description: `Access token is expired, do refresh`,
          })
          AnalyticsService.setContextVariable('authError', 'AccessTokenIsExpired')
          return reject('Refresh')
        }
        AnalyticsService.addBreadcrumb('AuthService.authenticate', {
          status: 'in process',
          description: `Access token is not expired`,
          expireToken: parsedToken.exp,
          now: Date.now()
        })
        this.userData = parsedToken
        const res = {
          access_token,
          refresh_token: AuthServiceCore.getRefreshToken(),
          userData: parsedToken,
        }
        AnalyticsService.identify(res.userData.segment_id, {
          email: res.userData?.email,
          username: res.userData?.username,
          id: res.userData?.segment_id,
          new_id: res.userData?.id,
        })
        AnalyticsService.setAuthStatus(true)
        AnalyticsService.addBreadcrumb('AuthService.authenticate', {
          status: 'resolve',
          description: `User data is collect, resolve`,
        })
        return resolve(res)
      } else {
        AnalyticsService.addBreadcrumb('AuthService.authenticate', {
          status: 'reject',
          description: `Tokens in not exist in cookie, make unauthorised`,
        })
        AnalyticsService.setContextVariable('authError', 'Unauthorised')
        return reject('Unauthorised')
      }
    })
  }

  logout() {
    this.userData = null
    AuthServiceCore.logout()
  }

  authorize(accessToken, refreshToken) {
    AnalyticsService.addBreadcrumb('AuthService', 'authorize')
    return new Promise((resolve, reject) => {
      try {
        const parsedToken = AuthServiceCore.parseToken(accessToken)
        const isParsedTokenValid = !!parsedToken?.exp
        if (!isParsedTokenValid) {
          AnalyticsService.setContextVariable('authError', 'Incorrect token')
          return reject('Incorrect token')
        }
        let cookieDomain = this.hostUrl.hostname
        if (!cookieDomain.match(WILDCARDS_DOMAIN)) {
          AnalyticsService.setContextVariable('authError', 'Unsupported domain')
          return reject('Unsupported domain')
        }

        this.setAccessToken(accessToken)
        this.setRefreshToken(refreshToken)

        if (parsedToken?.suspended) {
          AnalyticsService.addBreadcrumb('AuthService', 'authorize.response.suspended')
          AnalyticsService.setContextVariable('fromUrl', window.location.href)
          window.location.replace(this.suspendedUrl.toString())
          return reject('Suspended')
        }

        const result = {
          access_token: accessToken,
          refresh_token: refreshToken,
          userData: parsedToken
        }

        return resolve(result)
      } catch (e) {
        AnalyticsService.error(e)
        return reject(e)
      }
    })
  }

  setEnv(env) {
    Cookies.set(APP_ENVIRONMENT_COOKIE, env)
    this.env = env
  }

  setAccessToken(accessToken) {
    AnalyticsService.addBreadcrumb('AuthService', 'setAccessToken')
    let cookieDomain = AuthServiceCore.getDomain()
    if (!cookieDomain) {
      AnalyticsService.error(`Unsupported domain ${cookieDomain}`)
      return
    }
    Cookies.set(ACCESS_TOKEN_COOKIE, accessToken, {
      domain: cookieDomain,
      expires: ACCESS_TOKEN_DEFAULT_EXPIRE_TIME
    })
    return accessToken
  }

  setFingerprint(fingerprint) {
    AnalyticsService.addBreadcrumb('AuthService', 'setFingerprint')
    let cookieDomain = AuthServiceCore.getDomain()
    if (!cookieDomain) {
      AnalyticsService.error(`Unsupported domain ${cookieDomain}`)
      return
    }
    Cookies.set(FINGERPRINT_COOKIE, fingerprint, {
      domain: cookieDomain,
      expires: 30
    })

    return fingerprint
  }

  setRefreshToken(refreshToken) {
    AnalyticsService.addBreadcrumb('AuthService', 'setRefreshToken')
    let cookieDomain = AuthServiceCore.getDomain()
    if (!cookieDomain) {
      AnalyticsService.error(`Unsupported domain ${cookieDomain}`)
      return
    }
    Cookies.set(REFRESH_TOKEN_COOKIE, refreshToken, {
      domain: cookieDomain,
      expires: REFRESH_TOKEN_DEFAULT_EXPIRE_TIME
    })

    return refreshToken
  }

  clearSession() {
    AuthServiceCore.clearSessionCookie()
  }

  getRedirectUrl() {
    return AuthServiceCore.getRedirectUrl()
  }

  setRedirectUrl(url) {
    return AuthServiceCore.setRedirectUrl(url)
  }

  deleteRedirectUrl() {
    return AuthServiceCore.deleteRedirectUrl()
  }
}

export const AuthService = AuthServiceCore.getInstance()

export default AuthService
